<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue';
import CButtonIcon from '@/components/shared/CButtonIcon.vue';
import { faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { ButtonIconType } from '@/enums/ButtonIconType.enum';
import { ButtonIconColor } from '@/enums/ButtonIconColor.enum';
import { ButtonType } from '@/enums/ButtonType.enum';
import MapDownloadDropdown from '@/components/map/MapDownloadDropdown.vue';
import MapChart from '@/components/map/MapChart.vue';
import { downloadCSVContent } from '@/api/endpoints/map/map.endpoints';
import { downloadWithPattern, getFilenameFromHeaders } from '@/utils/utils';
import { downloadAttachment } from '@/utils/downloadAttachment.util';
import { jsPDF as JsPDF } from 'jspdf';
import { Chart, ChartDataset } from 'chart.js';
import { Filters } from '@/models/Filters.model';
import { MapCoordinates } from '@/api/endpoints/map/map.types';
import { LonLat } from '@/models/LonLat.model';
import CToggle from '@/components/shared/CToggle.vue';

enum ComponentEvent {
	HIDE_DETAILS = 'hideDetails',
	SHOW_DETAILS = 'showDetails',
	TOGGLE_GRID = 'toggleGrid',
}

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'MapDetails',
	components: { MapChart, MapDownloadDropdown, CButtonIcon, CToggle },
	props: {
		filters: {
			required: true,
			type: Object as PropType<Filters>,
		},
		fileId: {
			required: true,
			type: String,
		},
		showDetails: {
			required: false,
			type: Boolean,
			default: true,
		},
		datasets: {
			required: false,
			type: Array as PropType<ChartDataset<'line'>[]>,
			default: (): ChartDataset<'line'>[] => [],
		},
		chartTitle: {
			required: false,
			type: String,
			default: () => '',
		},
		yLabel: {
			required: false,
			type: String,
			default: () => '',
		},
		hasNoCsvData: {
			required: false,
			type: Boolean,
			default: () => false,
		},
		selectedFeatCoordinates: {
			required: false,
			type: Object as PropType<LonLat | null>,
			default: (): MapCoordinates | null => null,
		},
    showSubgrid: {
			required: true,
			type: Boolean,
			default: () => false,
		},
		subgridDisabled: {
			required: true,
			type: Boolean,
			default: () => false,
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	setup(props, context) {
		const visibility = ref<boolean>(props.showDetails);
		let myChart: Chart = null;

		const setVisibility = (value: boolean): void => {
			visibility.value = value;
			context.emit(value ? ComponentEvent.SHOW_DETAILS : ComponentEvent.HIDE_DETAILS);
		};

		watch(() => props.showDetails, setVisibility);

		const onChartUpdated = (chart: Chart): void => {
			myChart = chart;
		};

		const onDownloadImage = (): void => {
			if (!myChart) {
				return;
			}
			const { canvas } = myChart;

			downloadWithPattern({
				canvas,
				downloadName: props.chartTitle as string,
			});
		};

		const onDownloadPDF = (): void => {
			if (!myChart) {
				return;
			}
			const { canvas } = myChart;
			const imageData = canvas.toDataURL();

			const pdf = new JsPDF({ orientation: 'landscape', unit: 'mm' });
			const width = pdf.internal.pageSize.getWidth();
			const height = 41;

			pdf.addImage(imageData, 'PNG', 0, 23, width, height);
			pdf.save(`${props.chartTitle}.pdf`);
		};

		const onDownloadFile = async (): Promise<void> => {
			if (!props.selectedFeatCoordinates) {
				return;
			}

			const { data, headers } = await downloadCSVContent({
				uuid: props.fileId as string,
				lat: props.selectedFeatCoordinates.lat.toString(),
				lon: props.selectedFeatCoordinates.lon.toString(),
			});
			const name = getFilenameFromHeaders(headers) || 'chart';

			downloadAttachment(data, name);
		};

		const onToggleSubgrid = (): void => {
			context.emit(ComponentEvent.TOGGLE_GRID);
		};

		return {
			ButtonType,
			ButtonIconType,
			ButtonIconColor,
			faDownload,
			faTimes,
			visibility,
			setVisibility,
			onDownloadImage,
			onDownloadPDF,
			onDownloadFile,
			onChartUpdated,
      onToggleSubgrid,
		};
	},
});
</script>

<template>
	<transition name="fly-in">
		<div
			v-if="visibility"
			class="map-details"
		>
			<div class="flex-row flex-space width-100">
				<h3 class="map-details__tab m-0">
					<span>{{ chartTitle }}</span>
				</h3>

				<div class="map-details__actions flex-row ml-16">
					<map-download-dropdown
						v-if="!hasNoCsvData"
						:button-icon-type="ButtonIconType.FLAT"
						:button-icon-color="ButtonIconColor.SECONDARY"
						:hide-animation="true"
						:shadow-root="shadowRoot"
						@download-image="onDownloadImage()"
						@download-p-d-f="onDownloadPDF()"
						@download-file="onDownloadFile()"
					/>
					<c-button-icon
						:icon-name="faTimes.iconName"
						:type="ButtonIconType.FLAT"
						@click="setVisibility(false)"
					/>
				</div>
			</div>
			<div class="mt-12 flex-row">
				<c-toggle
					:model-value="showSubgrid"
					:label="$t('mapDetails.subgridToggleLabel')"
					:disabled="subgridDisabled"
					@update:model-value="onToggleSubgrid"
				/>
				<div class="map-details__subgrid-info flex-row items-center">
					<span>{{ showSubgrid ? $t('mapDetails.subgridToggleOffInfo') : $t('mapDetails.subgridToggleOnInfo') }}</span>
					<img v-if="!showSubgrid" class="map-details__subgrid-info-icon" src="@/assets/icons/nested-grid.svg" alt="Nested grid" />
				</div>
			</div>

			<div class="map-details__body">
				<div>
					<div v-if="hasNoCsvData" class="map-details__empty-state">
						<p>{{ $t('mapDetails.noCsvData') }}</p>
					</div>
					<map-chart
						v-else-if="datasets && visibility"
						:filters="filters"
						:dataset="datasets"
						:title="chartTitle"
						:y-label="yLabel"
						:shadow-root="shadowRoot"
						@chart-updated="onChartUpdated"
					/>
				</div>
			</div>
		</div>
	</transition>
</template>

<style scoped lang="scss">
.map-details {
  height: 40vh;

	padding: 12px 24px 24px 24px;
	background-color: tokens.$color-white;
}

.map-details__tab {
	display: flex;
	justify-content: flex-start;
	align-items: center;
}

.map-details__actions {
	margin-right: -12px;
}

.map-details__empty-state {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 200px;
	font-style: italic;
}

.map-details__subgrid-info {
  font-size: 12px;
  color: #6C757D;
  line-height: 1.5;
  position: relative;
  margin-left: 24px;

  &::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: #DEE2E6;
  }
}

.map-details__subgrid-info-icon {
  width: 14px;
  height: 14px;
  margin-left: 8px;
}

.fly-in-enter-active,
.fly-in-leave-active {
	transition: transform 0.4s ease-out;
}

.fly-in-enter-from,
.fly-in-leave-to {
	transform: translate3d(0, 100%, 0);
}
</style>
